import { type UpdateRadioStatusModel } from '@/shared/api'
import { attach, combine, createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'

import { radioSignalRService } from '../api/radio-signalr.service'
import {
  type RadioQueryFinishedSuccessResult,
  type RadioUpdateStart,
  radioQuery,
  radioUpdate,
} from '../api/radio.api'
import { DEFAULT_SEND_NOTIFICATION_TIME_IN_MINUTES } from '../lib/constants'

export const RadioGate = createGate()

export const radioSettingsUpdated = createEvent<RadioUpdateStart>()

const $radioSettingsData = createStore<
  Partial<RadioQueryFinishedSuccessResult>
>({
  radioScheduled: false,
  dateStarted: null,
  dateEnd: null,
  sendNotificationTimeInMinutes: DEFAULT_SEND_NOTIFICATION_TIME_IN_MINUTES,
  hasStarted: false,
  timerInSeconds: 0,
  trackFrequency: 0,
  podcastFrequency: 0,
  url: '',
  needToSendNotification: false,
})

export const $radioSettingsFormData = $radioSettingsData.map(
  (data): UpdateRadioStatusModel => ({
    needToSendNotification: Boolean(data.needToSendNotification),
    radioScheduled: Boolean(data.radioScheduled),
    endDate: data.dateEnd,
    startDate: data.dateStarted,
    sendNotificationTimeInMinutes:
      data.sendNotificationTimeInMinutes ??
      DEFAULT_SEND_NOTIFICATION_TIME_IN_MINUTES,
  }),
)

export const $radioHasStarted = $radioSettingsData.map(
  (data) => data.hasStarted,
)

export const $queryLoading = combine(
  {
    pending: radioQuery.$pending,
    data: radioQuery.$data,
    error: radioQuery.$error,
  },
  ({ pending, data, error }) => pending || (!data && !error),
)

export const $updateLoading = radioUpdate.$pending

export const radioStarted = createEvent()
export const radioEnded = createEvent()
const $signalRInstance = createStore(radioSignalRService)
const connectRadioStartedFx = attach({
  source: $signalRInstance,
  async effect(instance) {
    await instance.connect()
    instance.offRadioStarted()
    instance.onRadioStarted(radioStarted)
  },
})

const disconnectRadioStartedFx = attach({
  source: $signalRInstance,
  async effect(instance) {
    instance.offRadioStarted()
    await instance.disconnect()
  },
})
const connectRadioEndedFx = attach({
  source: $signalRInstance,
  async effect(instance) {
    await instance.connect()
    instance.offRadioEnded()
    instance.onRadioEnded(radioEnded)
  },
})

const disconnectRadioEndedFx = attach({
  source: $signalRInstance,
  async effect(instance) {
    instance.offRadioEnded()
    await instance.disconnect()
  },
})

sample({
  clock: [radioQuery.finished.success, radioUpdate.finished.success],
  fn: ({ result }) => result,
  target: $radioSettingsData,
})

sample({
  source: $radioSettingsData,
  clock: radioUpdate.finished.success,
  filter: ({ radioScheduled }, { params: { data } }) =>
    Boolean(data && radioScheduled !== data.radioScheduled),
})

sample({
  clock: radioSettingsUpdated,
  target: radioUpdate.start,
})

sample({
  clock: [radioStarted, radioEnded],
  target: radioQuery.start,
})

sample({
  clock: RadioGate.open,
  fn: () => {},
  target: [radioQuery.start, connectRadioStartedFx, connectRadioEndedFx],
})

sample({
  clock: RadioGate.close,
  target: [radioQuery.reset, disconnectRadioStartedFx, disconnectRadioEndedFx],
})
